import GraphyArrow from "@/assets/svgs/GraphyArrow";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type CarouselPropsType = {
  children: React.ReactNode;
};

const CAROUSEL_INTERVAL_TIME = 5000;
const NO_OF_PROGRESS_STEPS = 50;
const ATOMIC_PROGRESS_STEP = 100 / NO_OF_PROGRESS_STEPS;
const Carousel = (props: CarouselPropsType) => {
  const { children } = props;

  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const carouselIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const progressIntervalsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [carouselProgress, setCarouselProgress] =
    useState(ATOMIC_PROGRESS_STEP);
  const numberOfChildrens = React.Children.count(children);

  const setupProgressTimeouts = useCallback(() => {
    for (let i = 0; i < NO_OF_PROGRESS_STEPS; i++) {
      clearTimeout(progressIntervalsRef.current[i]);
    }
    for (let i = 0; i < NO_OF_PROGRESS_STEPS; i++) {
      progressIntervalsRef.current[i] = setTimeout(() => {
        setCarouselProgress((100 / NO_OF_PROGRESS_STEPS) * (i + 2));
      }, (CAROUSEL_INTERVAL_TIME / NO_OF_PROGRESS_STEPS) * (i + 1));
    }
  }, []);

  const startCarouselInterval = useCallback(() => {
    clearInterval(carouselIntervalRef.current);
    carouselIntervalRef.current = setInterval(() => {
      setupProgressTimeouts();
      setCarouselProgress(ATOMIC_PROGRESS_STEP);
      setActiveCarouselIndex((activeCarouselIndex + 1) % numberOfChildrens);
    }, CAROUSEL_INTERVAL_TIME);
  }, [
    activeCarouselIndex,
    numberOfChildrens,
    setupProgressTimeouts,
    setCarouselProgress,
  ]);

  const nextCarousel = useCallback(() => {
    setCarouselProgress(ATOMIC_PROGRESS_STEP);
    setActiveCarouselIndex((activeCarouselIndex + 1) % numberOfChildrens);
    startCarouselInterval();
  }, [activeCarouselIndex, numberOfChildrens, startCarouselInterval]);

  const previousCarousel = useCallback(() => {
    setCarouselProgress(ATOMIC_PROGRESS_STEP);
    setActiveCarouselIndex(
      Math.abs(activeCarouselIndex - 1 + numberOfChildrens) % numberOfChildrens
    );
    startCarouselInterval();
  }, [activeCarouselIndex, numberOfChildrens, startCarouselInterval]);

  const sliderControls = useMemo(() => {
    return Array.apply(null, Array(numberOfChildrens));
  }, [numberOfChildrens]);

  const carouselSliderClassName = useMemo(
    () => classNames("pointer-events-none flex w-full transition-transform"),
    []
  );

  useEffect(() => {
    startCarouselInterval();
    setupProgressTimeouts();
    return () => {
      clearInterval(carouselIntervalRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      progressIntervalsRef.current.forEach((interval) =>
        clearTimeout(interval)
      );
    };
  }, [startCarouselInterval, setupProgressTimeouts]);

  return (
    <div className="flex flex-col gap-6 overflow-x-hidden">
      <div
        className={classNames(
          "group/carousel-container pointer-events-none relative flex"
        )}
        style={{
          width: `${numberOfChildrens * 100}%`,
        }}
      >
        <div
          className={carouselSliderClassName}
          style={{
            transform: `translateX(-${
              (100 / numberOfChildrens) * activeCarouselIndex
            }%)`,
          }}
        >
          {children}
        </div>
      </div>
      <div className="flex justify-center gap-3">
        <GraphyArrow
          className="rotate-180 cursor-pointer rounded-full bg-grey-0"
          onClick={previousCarousel}
        />
        <div className="flex items-center gap-1">
          {sliderControls.map((control, index) => (
            <div
              className={classNames(
                "relative h-[5px] overflow-hidden rounded-full",
                {
                  "w-[50px] basis-[50px] bg-purple-2":
                    // "w-[20px] basis-[20px] bg-purple-3":
                    index === activeCarouselIndex,
                  "w-3 basis-3 bg-grey-0": index !== activeCarouselIndex,
                }
              )}
              key={index}
            >
              {index === activeCarouselIndex && (
                <div
                  className="absolute h-full w-full bg-purple-3 transition-transform ease-linear"
                  style={{
                    transform: `translateX(-${100 - carouselProgress}%)`,
                  }}
                />
              )}
            </div>
          ))}
        </div>
        <GraphyArrow
          className="cursor-pointer rounded-full bg-grey-0"
          onClick={nextCarousel}
        />
      </div>
    </div>
  );
};

export default Carousel;
